import axios, { AxiosRequestConfig } from "axios";
import { EditBaseClient, BaseClient, CrmClient } from "../../Types/BaseClientManagement";

const defaultAxiosPutPostConfig: AxiosRequestConfig = {
  headers: {
    "Content-Type": "application/json",
  },
};

export const getClients = async () => {
  try {
    const res = await axios.get<BaseClient[]>(import.meta.env.VITE_CLIENTSERVICE_API + "/GetClients");
    return res.data;
  } catch (error) {
    // Propagate so callers can show a real error state; a failed load must not
    // look like an empty client list.
    console.error(error);
    throw error;
  }
};

export const getClientsByProduct = async (productType: number) => {
  try {
    const res = await axios.get<BaseClient[]>(
      import.meta.env.VITE_CLIENTSERVICE_API + "/GetClientsByProduct/" + productType
    );
    return res.data;
  } catch (error) {
    console.error(error);
    return [] as BaseClient[];
  }
};

export const editClient = async (jsonData: EditBaseClient) => {
  try {
    await axios.put(import.meta.env.VITE_CLIENTSERVICE_API + "/client", jsonData, defaultAxiosPutPostConfig);
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const migrateClient = async (jsonData: EditBaseClient) => {
  try {
    await axios.post(
      import.meta.env.VITE_CLIENTSERVICE_API + "/client/migrateToStgDev",
      jsonData,
      defaultAxiosPutPostConfig
    );
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const createClient = async (jsonData: EditBaseClient) => {
  try {
    await axios.post(import.meta.env.VITE_CLIENTSERVICE_API + "/client", jsonData, defaultAxiosPutPostConfig);
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const deleteClient = async (clientId: string) => {
  try {
    await axios.delete(import.meta.env.VITE_CLIENTSERVICE_API + "/client/?clientId=" + clientId);
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const getClient = async (clientId: string) => {
  try {
    const res = await axios.get<BaseClient>(
      import.meta.env.VITE_CLIENTSERVICE_API + "/client/?clientId=" + clientId
    );
    return res.data;
  } catch (error) {
    console.error(error);
    return {} as BaseClient;
  }
};

/**
 * Fetches the Merge Link token and merge account Id for a given client
 * @param {string} clientId - the clientId
 * @returns {[string, string]} - the link token and merge account Id
 */
export const getMergeLinkToken = async (clientId: string) => {
  try {
    if (!!clientId) {
      const response = await axios.get<{ linkToken: string; mergeAccountId: string }>(
        `${import.meta.env.VITE_CLIENTSERVICE_API}/api/mergelink/token/${clientId}`
      );
      return [response.data.linkToken, response.data.mergeAccountId] as const;
    }
  } catch (err) {
    console.error(err);
  }
};

/**
 * Deletes the merge integration account with the given account Id for a given client
 * @param {string} clientId
 * @param {string} mergeAccountId
 * @returns
 */
export const deleteMergeIntegration = async (clientId: string, mergeAccountId: string) => {
  if (!!clientId && !!mergeAccountId) {
    try {
      await axios.delete(
        `${import.meta.env.VITE_CLIENTSERVICE_API}/api/mergelink/${clientId}/mergeaccount/${mergeAccountId}`
      );
    } catch (err) {
      console.error(err);
    }
  }
};

/**
 * Exchanges the public token with a private token on the backend for a given client
 * @param {string} clientId
 * @param {string} publicToken - token to exchange for an account token on the backend
 */
export const saveMergePublicToken = async (clientId: string, accountId: string, publicToken: string) => {
  if (!!clientId && !!accountId && !!publicToken) {
    try {
      return await axios.post(
        `${import.meta.env.VITE_CLIENTSERVICE_API}/api/mergelink/token/save`,
        {
          accountId,
          clientId,
          publicToken,
        },
        defaultAxiosPutPostConfig
      );
    } catch (err) {
      console.error(err);
    }
  }
};

export const getCrmClientInfo = async (clientId: string) => {
  try {
    if (!!clientId) {
      const response = await axios.get<CrmClient[]>(
        `${import.meta.env.VITE_CRM_API}/api/clients/${clientId}`
      );
      return response.data;
    }
  } catch (err) {
    console.error(err);
  }
};
